import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Trash2, Ban, ShieldAlert, Undo2 } from "lucide-react";
import { toast } from "sonner";

const NOUN = {
  case: "case", lender: "lending desk", referrer: "introducer",
  lender_application: "lender application", introducer_application: "introducer application",
  enquiry: "enquiry", message: "conversation", invoice: "invoice",
  career_application: "careers application",
};

/**
 * RemoveDialog — the one delete conversation in the Hub.
 * It asks the Bin what a delete would take with it, refuses outright when the Bin says no,
 * and makes clear the thing is recoverable for thirty days rather than gone.
 */
export function RemoveDialog({ kind, id, name, open, onOpenChange, onDeleted }) {
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const noun = NOUN[kind] || "record";

  useEffect(() => {
    if (!open || !id) { setInfo(null); setReason(""); return; }
    let live = true;
    api.get(`/hub/bin/preview/${kind}/${id}`)
      .then((r) => live && setInfo(r.data))
      .catch((e) => live && setInfo({ error: e?.response?.data?.detail || "Could not read that record" }));
    return () => { live = false; };
  }, [open, kind, id]);

  const blocked = !!(info?.blockers || []).length;

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hub/bin/delete", { kind, ids: [id], reason: reason.trim() || null });
      const extra = Object.values(data.cascade || {}).reduce((a, b) => a + b, 0);
      toast.success(`${name || noun} moved to the Bin${extra ? ` with ${extra} attached record${extra === 1 ? "" : "s"}` : ""}`, {
        description: `Restorable for ${data.hold_days} days from the Bin.`,
      });
      onOpenChange?.(false);
      onDeleted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="remove-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px]">Delete this {noun}?</DialogTitle>
          <DialogDescription className="text-[13px]">
            <span className="font-semibold text-slate-900">{name || info?.describes}</span> disappears
            from the Hub and from the partner portals straight away — and sits in the Bin for
            {" "}{info?.hold_days || 30} days in case you want it back.
          </DialogDescription>
        </DialogHeader>

        {!info ? (
          <div className="grid place-items-center py-8 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
        ) : info.error ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-600">{info.error}</div>
        ) : (
          <div className="space-y-3">
            {blocked && (
              <div className="flex items-start gap-2.5 rounded-xl border border-[#0055FF]/30 bg-[#0055FF]/[0.05] p-3.5 text-[12.5px] leading-relaxed text-[#0044CC]"
                   data-testid="remove-blocked">
                <Ban className="mt-0.5 size-4 shrink-0" />
                <div>{info.blockers.map((b) => <p key={b} className="mb-1 last:mb-0">{b}</p>)}</div>
              </div>
            )}

            {!!(info.takes_with_it || []).length && (
              <div className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid="remove-takes">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Goes with it</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {info.takes_with_it.map((t) => (
                    <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {!!(info.notes || []).length && (
              <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-[12px] leading-relaxed text-slate-600"
                   data-testid="remove-notes">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-slate-400" />
                <div>{info.notes.map((n) => <p key={n} className="mb-1 last:mb-0">{n}</p>)}</div>
              </div>
            )}

            {!blocked && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600">Reason (optional)</div>
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="Duplicate, hoax, test data, partner left…"
                       className="mt-1.5 h-9" data-testid="remove-reason" />
              </div>
            )}

            <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <Undo2 className="size-3" /> A line is written to the deletion log with your name on it.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy}
                  className="rounded-full" data-testid="remove-cancel">
            {blocked ? "Close" : "Cancel"}
          </Button>
          {!blocked && (
            <Button onClick={go} disabled={busy || !info || info.error}
                    className="rounded-full bg-red-500 hover:bg-red-600" data-testid="remove-confirm">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <><Trash2 className="mr-1.5 size-3.5" /> Move to Bin</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
